import { useState, useEffect, useRef } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import { 
  Building2, 
  Crown, 
  Users, 
  AlertTriangle, 
  Trash2, 
  // CheckCircle2, -- used by the hidden Data Export card
  Check,
  ChevronUp,
  ChevronDown
} from 'lucide-react'
import { Button, LoadingButton, ComboBox, ConfirmationModal, Input, Card, CardHeader, CardTitle, CardDescription, CardContent, DescriptionList, SettingsSaveBar, useDirtyForm, useToastActions, PageHeader } from '@airclou/ui'
import type { ComboBoxItem } from '@airclou/ui'
import { DeleteOrganizationModal } from './components/DeleteOrganizationModal'
import { OrganizationDeletedModal } from './components/OrganizationDeletedModal'
import { TransferOwnershipModal } from './components/TransferOwnershipModal'
import { getServiceApiUrl } from '@airclou/utils'
import { HTTP_TIMEOUT_MS } from '../../config'
import { dashboardApiClient } from '../../services/api'
import type { TenantErasurePreview } from '../../services/api'
import { fileService, FileUploadErrorCode } from '../../services/fileService'
import { orphanedLogoFiles } from './logoFileLifecycle'

interface OrganizationForm {
  name: string
  email: string
  logoUrl: string | null
}

const EMPTY_ORGANIZATION: OrganizationForm = { name: '', email: '', logoUrl: null }

export function OrganizationPage() {
  const { t } = useTranslation('dashboard')
  const { user, logout, isLoading: isAuthLoading } = useAuth()
  const { success: toastSuccess, error: toastError } = useToastActions()
  
  const [createdDate] = useState('January 15, 2024')
  const [memberCount] = useState(42)

  // Name, email and logo are one explicit-Save form. The logo is a working
  // draft like the text fields: an upload stores the file straight away, but
  // the address only reaches the tenant record on Save, so Discard puts the
  // previous logo back rather than destroying the stored one.
  const form = useDirtyForm<OrganizationForm>(EMPTY_ORGANIZATION)
  const { values, baseline, isDirty: hasChanges } = form
  const organizationName = baseline.name
  const organizationEmail = baseline.email

  const [isSaving, setIsSaving] = useState(false)
  const [isUploadingLogo, setIsUploadingLogo] = useState(false)
  const [logoError, setLogoError] = useState<string | null>(null)
  // Logos uploaded during this edit that no save has committed. The file is on
  // disk the moment it uploads, but only Save writes its address to the tenant
  // record — so until then nothing points at it. Discard drops them; Save drops
  // the ones it did not commit. The rule itself lives in `logoFileLifecycle`.
  const uncommittedUploads = useRef<string[]>([])

  /**
   * End the edit: clear the pending-upload list and delete whatever it leaves
   * unreferenced. Call this on every path that ends an edit, or a file uploaded
   * and then backed out of is left behind.
   *
   * Deleting is best-effort — an orphaned file is untidy, a save reported as
   * failed when it actually succeeded is worse — so failures are logged, never
   * surfaced.
   */
  const flushUncommittedUploads = async (committedUrl: string | null, supersededUrl?: string | null) => {
    const orphans = orphanedLogoFiles({
      uploadedThisEdit: uncommittedUploads.current,
      committedUrl,
      supersededUrl,
    })
    uncommittedUploads.current = []
    for (const url of orphans) {
      try {
        await dashboardApiClient.deleteFile(url)
      } catch (deleteError) {
        console.error('Failed to remove an unreferenced logo file:', url, deleteError)
      }
    }
  }
  
  // Ownership transfer state
  const [selectedOwnerItems, setSelectedOwnerItems] = useState<ComboBoxItem[]>([])
  const selectedNewOwner = selectedOwnerItems[0] ?? null
  const [showTransferModal, setShowTransferModal] = useState(false)
  const [isTransferring, setIsTransferring] = useState(false)
  const [transferStatus, setTransferStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [transferError, setTransferError] = useState<string | null>(null)
  const [confirmationPhrase, setConfirmationPhrase] = useState<string>('')
  const [showRefreshModal, setShowRefreshModal] = useState(false)
  const [transferredToAdmin, setTransferredToAdmin] = useState('')
  const [isOwnershipExpanded, setIsOwnershipExpanded] = useState(false)

  /**
   * The organization's real admins, from the members API.
   *
   * This used to be four hardcoded fictional people, so the picker offered
   * names that did not exist and the transfer it fed was a 1.5-second sleep.
   * `viewer` and `billing_admin` are deliberately not offered: ownership carries
   * full control, and handing it to a read-only member or a billing-only one
   * would leave the organization unmanageable by its own owner.
   */
  const fetchAdminUsers = async (query: string, limit: number, offset: number, exclude?: string[]) => {
    const page = Math.floor(offset / limit) + 1
    const response = await dashboardApiClient.getTeamMembers({
      page,
      limit,
      search: query || undefined,
      role: 'admin',
    })

    const excluded = exclude || []
    const items: ComboBoxItem[] = response.data
      // The current owner cannot be handed the organization they already own,
      // and the server refuses it — so keep them out of the list rather than
      // offering a choice that answers ALREADY_OWNER.
      .filter(m => m.user_id !== user?.id && !excluded.includes(m.user_id))
      .map(m => ({
        id: m.user_id,
        name: m.name || m.email,
        email: m.email,
        role: m.tenant_role,
        department: '',
      }))

    return {
      items,
      total: response.meta.total,
      hasMore: response.meta.page < response.meta.totalPages,
    }
  }

  const generateRandomPhrase = () => {
    const phrases = [
      'transfer ownership permanently',
      'confirm ownership change',
      'irreversible account transfer',
      'organization ownership transfer',
      'account ownership change'
    ]
    return phrases[Math.floor(Math.random() * phrases.length)]
  }

  const getSelectedAdminName = () => {
    return selectedNewOwner ? selectedNewOwner.name : ''
  }

  const handleTransferOwnership = () => {
    if (!selectedNewOwner) return
    const phrase = generateRandomPhrase()
    setConfirmationPhrase(phrase)
    // Clear the previous attempt's failure. Without this, reopening the dialog
    // shows the reason the *last* transfer failed as though it applied to this
    // one — and the inline message beside the button stays up behind it.
    setTransferStatus('idle')
    setTransferError(null)
    setShowTransferModal(true)
  }
  
  const handleConfirmTransfer = async () => {
    if (!selectedNewOwner) return
    if (!user?.tenantId) return
    setIsTransferring(true)
    setTransferStatus('idle')
    setTransferError(null)
    try {
      await dashboardApiClient.transferOwnership(user.tenantId, String(selectedNewOwner.id))
      setTransferredToAdmin(selectedNewOwner.name)
      setShowTransferModal(false)
      // The caller is an `admin` from here on, so the page they are looking at
      // is describing permissions they no longer hold. Reloading is not a
      // nicety — it is how the UI stops offering them owner-only controls.
      setShowRefreshModal(true)
      setSelectedOwnerItems([])
      setConfirmationPhrase('')
    } catch (error) {
      // Branch on the server's code, never the message.
      const code = (error as { code?: string }).code
      setTransferError(
        code === 'MEMBER_NOT_FOUND' ? t('organization.transferMemberNotFound')
        : code === 'MEMBER_NOT_ACTIVE' ? t('organization.transferMemberNotActive')
        : code === 'OWNERSHIP_CHANGED' ? t('organization.transferOwnershipChanged')
        : code === 'ALREADY_OWNER' ? t('organization.transferAlreadyOwner')
        : t('organization.failedToTransfer'),
      )
      setTransferStatus('error')
    } finally {
      setIsTransferring(false)
    }
  }

  // Delete organization state
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  // Held in memory from the DELETE response: once it lands, the organization is
  // gone and no later request could fetch this again.
  const [deleteResult, setDeleteResult] = useState<{ purgeAfter: string; erasurePreview: TenantErasurePreview | null } | null>(null)
  // Drawn while the organization is still live, so the confirmation modal can
  // show what deleting it costs BEFORE the owner confirms. Best-effort: if it
  // cannot be counted the modal says so and the delete still works — an owner
  // must be able to close their account when a service is down.
  const [erasurePreview, setErasurePreview] = useState<TenantErasurePreview | null>(null)
  const [isPreviewLoading, setIsPreviewLoading] = useState(false)
  const [previewFailed, setPreviewFailed] = useState(false)

  // Ownership transfer and deletion are the owner's, and only the owner's.
  //
  // Read from `orgRole`, NOT `role`. `role` is the dashboard app-access role,
  // a different vocabulary that never says 'owner' — gating on it would have
  // hidden both controls from the actual owner. `orgRole` is `tenant_users.role`
  // for the session tenant, which is exactly what `requireTenantRole` resolves,
  // so what this hides is what the API would refuse.
  //
  // It also used to read `billing_admin`, which is a side role for plan and
  // billing — explicitly NOT a rank above admin (see TENANT_ROLE_SETS).
  const isOwner = user?.orgRole === 'owner'

  // Three states, not two. `orgRole` is fetched from /context on every load, so
  // it is undefined while that is in flight AND when it failed — and "we do not
  // know your role" is not the same claim as "you are not the owner". Telling an
  // owner they are not one, because a network call blipped, is the same error as
  // a settings row announcing a second factor is off when the read merely
  // failed. The fields stay disabled either way, since that is the safe
  // default; only the explanation changes.
  const roleKnown = !isAuthLoading && user?.orgRole !== undefined

  // Fetch organization data including logo on mount
  useEffect(() => {
    const fetchOrganizationData = async () => {
      try {
        const token = localStorage.getItem('dashboard:authToken')
        const tenantId = user?.tenantId
        
        if (!tenantId) return

        const response = await fetch(`${getServiceApiUrl('dashboard')}/tenants/${tenantId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          signal: AbortSignal.timeout(HTTP_TIMEOUT_MS),
        })

        if (response.ok) {
          const data = await response.json()

          if (data.success && data.data) {
            const tenant = data.data
            form.setBaseline({
              name: tenant.name || '',
              email: tenant.email || '',
              logoUrl: tenant.logo_url || null,
            })
          }
        }
      } catch (error) {
        console.error('Error fetching organization data:', error)
      }
    }

    fetchOrganizationData()
  }, [user?.tenantId])

  // Counted when the modal opens, not on mount: it makes five services count
  // rows, so it runs when someone is actually considering deleting.
  useEffect(() => {
    if (!showDeleteModal || !user?.tenantId) return
    let cancelled = false
    setIsPreviewLoading(true)
    setPreviewFailed(false)
    dashboardApiClient
      .getErasurePreview(user.tenantId)
      .then((preview) => {
        if (!cancelled) setErasurePreview(preview)
      })
      .catch((error) => {
        // Never surfaced raw: it names services and tables the owner cannot act
        // on. The modal shows one plain sentence and keeps the delete enabled.
        console.error('[OrganizationPage] erasure preview failed:', error)
        if (!cancelled) {
          setErasurePreview(null)
          setPreviewFailed(true)
        }
      })
      .finally(() => {
        if (!cancelled) setIsPreviewLoading(false)
      })
    // Closing the modal mid-flight must not leave a late response writing into
    // a dialog the owner has already dismissed.
    return () => {
      cancelled = true
    }
  }, [showDeleteModal, user?.tenantId])

  const handleDeleteOrganization = () => {
    // Same reason as the transfer dialog: a stale failure from the last attempt
    // must not be presented as the outcome of this one.
    setDeleteError(null)
    setShowDeleteModal(true)
  }
  
  const handleConfirmDelete = async (confirmName: string) => {
    if (!user?.tenantId) return
    setIsDeleting(true)
    setDeleteError(null)
    try {
      const result = await dashboardApiClient.deleteOrganization(user.tenantId, confirmName)
      setShowDeleteModal(false)
      // The organization is gone from under this session: every request from
      // here answers TENANT_DELETED and the sessions have been revoked. The one
      // thing left to do is show the owner what the erasure will remove — from
      // this response, because nothing can fetch it afterwards — and then sign
      // out. Every way off that screen signs out (see OrganizationDeletedModal);
      // staying on a settings page for an organization that no longer exists
      // would just produce errors.
      setDeleteResult({ purgeAfter: result.purgeAfter, erasurePreview: result.erasurePreview ?? null })
    } catch (error) {
      const code = (error as { code?: string }).code
      setDeleteError(
        code === 'CONFIRM_NAME_MISMATCH' ? t('organization.deleteNameMismatch')
        : code === 'BILLING_CANCEL_FAILED' ? t('organization.deleteBillingFailed')
        : code === 'TENANT_ALREADY_DELETED' ? t('organization.deleteAlreadyDeleted')
        : t('organization.deleteFailed'),
      )
    } finally {
      setIsDeleting(false)
    }
  }
  
  /* Data Export is hidden; see the commented-out card below.
  const handleExportData = () => {
    alert(t('organization.dataExportFuture'))
  }
  */

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    setIsUploadingLogo(true)
    setLogoError(null)

    try {
      // Upload logo file using shared file service
      const uploadResult = await fileService.uploadOrganizationLogo(file)

      if (!uploadResult.success) {
        const errorCode = uploadResult.error?.code
        if (errorCode === FileUploadErrorCode.FILE_TOO_LARGE) {
          setLogoError(t('organization.fileTooLarge'))
        } else if (errorCode === FileUploadErrorCode.INVALID_MIME_TYPE) {
          setLogoError(t('organization.invalidFileType'))
        } else {
          setLogoError(uploadResult.error?.message || t('organization.failedToUploadLogo'))
        }
        return
      }

      const newLogoUrl = uploadResult.data.url

      // Stage the uploaded logo as a pending change; it persists (alongside
      // name/email) when the user clicks Save. This re-enables Save Changes.
      uncommittedUploads.current.push(newLogoUrl)
      form.setField('logoUrl', newLogoUrl)
    } catch (error) {
      console.error('Logo upload error:', error)
      setLogoError(error instanceof Error ? error.message : t('organization.failedToUploadLogo'))
    } finally {
      setIsUploadingLogo(false)
      // Reset file input
      event.target.value = ''
    }
  }

  const handleLogoRemove = () => {
    // Stage removal as a pending change; it persists on Save (consistent with
    // upload). Avoids destroying the stored logo before the user commits.
    setLogoError(null)
    form.setField('logoUrl', null)
  }

  const handleFormChange = (field: 'name' | 'email', value: string) => {
    form.setField(field, value)
  }

  const handleSave = async () => {
    const trimmedName = values.name.trim()
    const trimmedEmail = values.email.trim()

    if (!trimmedName) {
      toastError(t('organization.nameCannotBeEmpty'))
      return
    }

    if (trimmedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      toastError(t('organization.invalidEmailAddress'))
      return
    }

    // Only send fields that changed across the whole form (name, email, logo).
    const updates: Record<string, string | null> = {}
    if (trimmedName !== organizationName) updates.name = trimmedName
    if (trimmedEmail !== organizationEmail) updates.email = trimmedEmail || null
    const logoChanged = values.logoUrl !== baseline.logoUrl

    if (Object.keys(updates).length === 0 && !logoChanged) {
      // Nothing but whitespace changed. Adopt the trimmed values so the form
      // stops reporting itself as dirty.
      form.setBaseline({ ...baseline, name: trimmedName, email: trimmedEmail })
      // This path still ends the edit, so anything uploaded during it and then
      // backed out of is now unreferenced — upload a logo, remove it again, fix
      // a stray space in the name, Save. Without this the file stays on disk
      // with nothing pointing at it.
      void flushUncommittedUploads(values.logoUrl)
      return
    }

    setIsSaving(true)
    // What the tenant record holds once this save lands. The name/email PATCH
    // answers with the stored row, so prefer its values over the typed ones.
    let savedName = trimmedName
    let savedEmail = trimmedEmail || ''
    try {
      const token = localStorage.getItem('dashboard:authToken')
      const tenantId = user?.tenantId

      // Persist name/email (if changed).
      if (Object.keys(updates).length > 0) {
        const response = await fetch(`${getServiceApiUrl('dashboard')}/tenants/${tenantId}`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          signal: AbortSignal.timeout(HTTP_TIMEOUT_MS),
          body: JSON.stringify(updates),
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.error?.code || errorData.code || 'UPDATE_FAILED')
        }

        const data = await response.json()
        if (data.success && data.data) {
          const tenant = data.data
          savedName = tenant.name || ''
          savedEmail = tenant.email || ''
        }
      }

      // Persist logo (if changed).
      if (logoChanged) {
        const response = await fetch(`${getServiceApiUrl('dashboard')}/tenants/${tenantId}/logo`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          signal: AbortSignal.timeout(HTTP_TIMEOUT_MS),
          body: JSON.stringify({ logo_url: values.logoUrl }),
        })

        if (!response.ok) {
          throw new Error('LOGO_UPDATE_FAILED')
        }
      }

      const supersededLogoUrl = baseline.logoUrl
      form.setBaseline({ name: savedName, email: savedEmail, logoUrl: values.logoUrl })
      toastSuccess(t('organization.settingsUpdated'))

      // The record now points at the new address, so what it displaced is
      // unreferenced: the logo the tenant held before this save, plus any file
      // uploaded during this edit that is not the one just committed.
      await flushUncommittedUploads(values.logoUrl, supersededLogoUrl)
    } catch (error) {
      const code = error instanceof Error ? error.message : 'UPDATE_FAILED'
      if (code === 'INVALID_NAME') {
        toastError(t('organization.nameCannotBeEmpty'))
      } else if (code === 'NAME_TOO_LONG') {
        toastError(t('organization.nameTooLong'))
      } else if (code === 'INVALID_EMAIL') {
        toastError(t('organization.invalidEmailAddress'))
      } else if (code === 'LOGO_UPDATE_FAILED') {
        toastError(t('organization.failedToUpdateLogo'))
      } else if (code === 'INSUFFICIENT_TENANT_ROLE' || code === 'NOT_TENANT_MEMBER') {
        // The fields above are already disabled for non-owners, so reaching
        // here means the role changed underneath this tab — ownership handed
        // on, or the seat downgraded, while the page sat open. `orgRole` came
        // from the session and does not re-read itself. A generic "could not
        // update" would leave the operator retrying a save that cannot ever
        // succeed, so name the cause and point at the fix, which is a reload.
        toastError(t('organization.roleChangedReload'))
      } else {
        toastError(t('organization.failedToUpdate'))
      }
    } finally {
      setIsSaving(false)
    }
  }

  const handleReset = () => {
    // The committed logo stays on disk — Discard has to be able to show it
    // again. The files uploaded during this edit are the opposite case: no save
    // committed them, so nothing points at them and nothing ever will.
    form.reset()
    setLogoError(null)
    void flushUncommittedUploads(baseline.logoUrl)
  }

  return (
    <div className="space-y-6">
      <PageHeader title={t('organization.title')} description={t('organization.subtitle')} />

      <div className="max-w-2xl space-y-6">
        {/* General Settings */}
        <div className="bg-card rounded-lg border border-border">
          <div className="p-6 border-b border-border">
            <h3 className="text-base font-semibold text-foreground">{t('organization.generalSettings')}</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {t('organization.basicInfo')}
            </p>
          </div>
          <div className="p-6 space-y-6">
            {/* Organization Logo */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('organization.orgLogo')}
              </label>
              <div className="flex items-start gap-4">
                {/* The dashed border + tinted fill are the empty-state drop
                    affordance; once a logo is set they'd frame the image. */}
                <div
                  className={`w-20 h-20 rounded-lg flex items-center justify-center overflow-hidden ${
                    values.logoUrl
                      ? ''
                      : 'border-2 border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800'
                  }`}
                >
                  {values.logoUrl ? (
                    <img 
                      src={values.logoUrl.startsWith('http') ? values.logoUrl : `${getServiceApiUrl('dashboard').replace('/api', '')}${values.logoUrl}`} 
                      alt="Organization logo" 
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <Building2 className="h-8 w-8 text-gray-400" />
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  {/* Blocked while a save is in flight as well as during an
                      upload. The save works out which uploaded files are
                      unreferenced from the address it is committing, and a
                      file that lands after it read that address is not in it
                      — so the save would delete a logo the form is still
                      showing. The save bar already blocks Save during an
                      upload; this is the same guard the other way round. */}
                  <div className="flex items-center gap-2">
                    <label htmlFor="logo-upload">
                      <input
                        id="logo-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        disabled={isUploadingLogo || isSaving}
                        className="hidden"
                      />
                      <LoadingButton
                        onClick={() => document.getElementById('logo-upload')?.click()}
                        loading={isUploadingLogo}
                        loadingText={t('common.loading')}
                        disabled={isUploadingLogo || isSaving}
                        variant="outline"
                        size="sm"
                      >
                        {values.logoUrl ? t('organization.changeLogo') : t('organization.uploadLogo')}
                      </LoadingButton>
                    </label>
                    {values.logoUrl && (
                      <Button
                        onClick={handleLogoRemove}
                        disabled={isUploadingLogo || isSaving}
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                      >
                        {t('common.remove')}
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t('organization.logoHint')}
                  </p>
                  {logoError && (
                    <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      {logoError}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Organization Name.

                Read-only for anyone but the owner, because the server now
                refuses the write (§6: the org's name is its identity, so
                PATCH /:id is OWNER_ONLY while the logo beside it takes
                MANAGE). An admin reaches this page legitimately — they can
                still change the logo — so the field has to say why it is
                closed rather than accept edits and fail on save. */}
            <div>
              <label htmlFor="org-name" className="block text-sm font-medium text-foreground mb-2">
                {t('organization.orgName')}
              </label>
              <Input
                id="org-name"
                type="text"
                value={values.name}
                onChange={(value) => handleFormChange('name', value)}
                placeholder="e.g., Acme Inc."
                disabled={isSaving || !isOwner}
              />
            </div>

            {/* Organization Email */}
            <div>
              <label htmlFor="org-email" className="block text-sm font-medium text-foreground mb-2">
                {t('organization.orgEmail')}
              </label>
              <Input
                id="org-email"
                type="text"
                value={values.email}
                onChange={(value) => handleFormChange('email', value)}
                placeholder="e.g., admin@company.com"
                disabled={isSaving || !isOwner}
              />
            </div>

            {roleKnown && !isOwner && (
              <p className="text-sm text-muted-foreground">
                {t('organization.onlyOwnerCanEditIdentity')}
              </p>
            )}
            {!roleKnown && !isAuthLoading && (
              <p className="text-sm text-muted-foreground">
                {t('organization.roleUnknownReload')}
              </p>
            )}

          </div>
        </div>

        {/* Organization Information (read-only) */}
        <DescriptionList
          title={t('organization.orgInfo')}
          description={t('organization.additionalDetails')}
          items={[
            {
              term: t('organization.orgId'),
              description: (
                <code className="text-xs font-mono bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                  {user?.tenantId || 'N/A'}
                </code>
              ),
            },
            {
              term: t('common.created'),
              description: createdDate,
            },
            {
              term: t('organization.totalMembers'),
              description: (
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <span>{t('organization.membersCount', { count: memberCount })}</span>
                </div>
              ),
            },
          ]}
        />

        {/* Ownership Transfer (billing_admin only) */}
        {isOwner && (
          <Card>
            <CardHeader className={`space-y-1 border-b ${isOwnershipExpanded ? 'border-border' : 'border-transparent'}`}>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base leading-6">
                  {t('organization.ownershipTransfer')}
                </CardTitle>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setIsOwnershipExpanded(!isOwnershipExpanded)}
                  className="h-6 w-6 p-0"
                  aria-label={t('organization.ownershipTransfer')}
                  aria-expanded={isOwnershipExpanded}
                >
                  {isOwnershipExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </Button>
              </div>
              <CardDescription>
                {isOwnershipExpanded ? t('organization.transferToAdmin') : t('organization.expandOwnershipOptions')}
              </CardDescription>
            </CardHeader>
            {isOwnershipExpanded && (
              <CardContent className="pt-6">
                <div className="space-y-4">
                  {/* Current Owner */}
                  <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary/15 dark:bg-primary/25 rounded-full flex items-center justify-center">
                        <Crown className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-sm text-muted-foreground mb-1">{t('organization.currentOwner')}</p>
                        <p className="text-base font-semibold">{user?.name || t('organization.unknownUser')}</p>
                        <p className="text-sm text-muted-foreground">{user?.email || t('organization.noEmail')}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-3 py-1 bg-primary/15 dark:bg-primary/25 text-primary dark:text-[hsl(var(--primary-hover))] text-xs font-medium rounded-full">
                        {t('roles.billingAdmin')}
                      </span>
                    </div>
                  </div>

                  {/* Transfer Ownership */}
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Users className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                      <h4 className="font-medium text-sm">{t('organization.transferOwnership')}</h4>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">
                      {t('organization.transferWarning')}
                    </p>
                    
                    <div className="space-y-3">
                      <div>
                        <ComboBox
                          id="selectNewOwner"
                          label={t('organization.selectNewOwner')}
                          fetchItems={fetchAdminUsers}
                          selectedItems={selectedOwnerItems}
                          onSelectionChange={setSelectedOwnerItems}
                          multiSelect={false}
                          placeholder={t('organization.searchSelectAdmin')}
                          className="max-w-md"
                        />
                      </div>
                      
                      {selectedNewOwner && (
                        <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                          <div className="flex items-start gap-2">
                            <AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-400 mt-0.5" />
                            <div className="text-sm">
                              <p className="font-medium text-yellow-800 dark:text-yellow-200">
                                {t('organization.importantCannotBeUndone')}
                              </p>
                              <p className="text-yellow-700 dark:text-yellow-300 mt-1">
                                <Trans t={t} i18nKey="organization.transferConsequence" values={{ name: getSelectedAdminName() }} />
                              </p>

                            </div>
                          </div>
                        </div>
                      )}
                      
                      <div className="flex items-center gap-2">
                        <LoadingButton
                          onClick={handleTransferOwnership}
                          loading={isTransferring}
                          loadingText={t('organization.transferring')}
                          disabled={!selectedNewOwner}
                          variant="outline"
                          className="text-red-600 border-red-300 hover:bg-red-50 hover:border-red-400 dark:text-red-400 dark:border-red-800 dark:hover:bg-red-900/20"
                        >
                          <Crown className="h-4 w-4 mr-2" />
                          {t('organization.transferOwnership')}
                        </LoadingButton>
                        
                        {transferStatus === 'error' && (
                          <div className="flex items-center gap-1 text-red-600 dark:text-red-400 text-sm">
                            <AlertTriangle className="h-4 w-4" />
                            <span>{t('organization.transferFailed')}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            )}
          </Card>
        )}

        {/* Ownership Notice - Only show for non-billing admins */}
        {!isOwner && (
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="flex items-center gap-2">
              <Crown className="h-4 w-4 text-primary" />
              <p className="text-sm text-primary">
                <span className="font-medium">{t('organization.organizationOwnership')}</span> {t('organization.ownershipNotice')}
              </p>
            </div>
          </div>
        )}

        {/* Data Export -- Hidden 2026-10-05: the button only said "will be implemented in a
            future release", and nothing exports playbooks, forms, users or billing.
            Parked in docs/settings-open-items.md §10.
        <Card>
          <CardHeader className="space-y-1 border-b border-border">
            <CardTitle className="text-base leading-6">{t('organization.dataExport')}</CardTitle>
            <CardDescription>{t('organization.dataExportDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-5 w-5 text-primary mt-0.5" />
                <div className="text-sm text-primary">
                  <p className="font-medium mb-1">{t('organization.dataExportIncludes')}</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>{t('organization.exportPlaybooks')}</li>
                    <li>{t('organization.exportForms')}</li>
                    <li>{t('organization.exportUsers')}</li>
                    <li>{t('organization.exportBilling')}</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <Button onClick={handleExportData}>
              {t('organization.exportData')}
            </Button>
          </CardContent>
        </Card>
        */}

        {/* Danger Zone (owner only) */}
        {isOwner && (
          <Card className="border-2 border-red-200 dark:border-red-800">
            <CardHeader className="space-y-1 border-b border-red-200 dark:border-red-800">
              <CardTitle className="flex items-center gap-2 text-base leading-6 text-red-600 dark:text-red-400">
                <AlertTriangle className="h-4 w-4" />
                {t('organization.dangerZone')}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-4">
                <div className="border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <h4 className="font-medium text-red-600 dark:text-red-400 mb-2">{t('organization.deleteOrganization')}</h4>
                  {/* Says what deletion does, which is shut the organization
                      down — not erase it. The five-item list that used to sit
                      here (playbooks, forms, users, billing, integrations)
                      described a cross-service purge that does not exist;
                      only the billing line was ever true. */}
                  <p className="text-sm text-muted-foreground mb-3">
                    {t('organization.deleteOrgDescription')}
                  </p>
                  <ul className="list-disc list-inside text-sm text-muted-foreground mb-4 space-y-1">
                    <li>{t('organization.deleteEffectAccess')}</li>
                    <li>{t('organization.deleteEffectBilling')}</li>
                    <li>{t('organization.deleteEffectData')}</li>
                  </ul>
                  <p className="text-sm font-semibold text-red-600 dark:text-red-400 mb-4">
                    {t('organization.deleteRestoreNote')}
                  </p>
                  <Button
                    onClick={handleDeleteOrganization}
                    variant="outline"
                    disabled={isSaving}
                    className="text-red-600 border-red-300 hover:bg-red-50 hover:border-red-400 dark:text-red-400 dark:border-red-800 dark:hover:bg-red-900/20"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {t('organization.deleteOrganization')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Inside the capped form container, not after it: the bar should be
            as wide as the thing it commits, and this container is tall enough
            for `position: sticky` to travel within. */}
        <SettingsSaveBar
          visible={hasChanges}
          onSave={handleSave}
          onDiscard={handleReset}
          isSaving={isSaving}
          saveDisabled={isDeleting || isUploadingLogo}
          message={t('common.unsavedChanges')}
          saveLabel={t('common.save')}
          savingLabel={t('common.saving')}
          discardLabel={t('common.discard')}
        />
      </div>

      <TransferOwnershipModal
        isOpen={showTransferModal}
        onClose={() => setShowTransferModal(false)}
        onConfirm={handleConfirmTransfer}
        confirmationPhrase={confirmationPhrase}
        newOwnerName={getSelectedAdminName()}
        newOwnerEmail={selectedNewOwner?.email as string | undefined}
        isTransferring={isTransferring}
        errorMessage={transferStatus === 'error' ? (transferError ?? t('organization.failedToTransfer')) : null}
      />

      {/* Post-Transfer Refresh Modal */}
      <ConfirmationModal
        isOpen={showRefreshModal}
        onClose={() => setShowRefreshModal(false)}
        title={t('organization.ownershipTransferred')}
        variant="success"
        confirmText={t('organization.refreshNow')}
        cancelText={t('common.close')}
        onConfirm={() => window.location.reload()}
        onCancel={() => setShowRefreshModal(false)}
        icon={Check}
      >
        <p className="text-sm text-gray-700 dark:text-gray-300">
          <Trans t={t} i18nKey="organization.transferredToRefresh" values={{ name: transferredToAdmin }} />
        </p>
      </ConfirmationModal>

      {/* Delete Organization Modal */}
      <DeleteOrganizationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        organizationName={organizationName}
        organizationEmail={organizationEmail}
        isDeleting={isDeleting}
        errorMessage={deleteError}
        erasurePreview={erasurePreview}
        isPreviewLoading={isPreviewLoading}
        previewFailed={previewFailed}
      />

      {/* Shown once the organization is deleted; the only exit is signing out.
          Through the auth context, not `window.location.href = '/logout'`: there
          is no such route in this app, so that would have landed the operator on
          a not-found page still holding a token. `logout()` invalidates the
          session cookie, clears every app's keys on this origin, tells the other
          tabs, and then navigates. */}
      {deleteResult && (
        <OrganizationDeletedModal
          isOpen
          purgeAfter={deleteResult.purgeAfter}
          erasurePreview={deleteResult.erasurePreview}
          onSignOut={logout}
        />
      )}
    </div>
  )
}
